// Collector: raw MSSQL source → live syntax-error markers.
//
// The in-house replacement for SqlToolsService's `textDocument/publishDiagnostics`
// on mssql (Plan 20 §10 step 2). Emit source is the vendored ANTLR
// `sql-grammar-tsql` parser; false positives are killed suppression-first with
// four independent layers (Plan 20 Decision 2):
//
//   (a) AND-of-two-parsers — an ANTLR error is emitted ONLY where it overlaps a
//       tree-sitter `buildErrorRecoveryView` errorRange (confidence 1.0) for the
//       same source. The two grammars have DIFFERENT gap sets, so a
//       legal-but-unsupported construct that only ONE parser rejects is never
//       marked.
//   (b) EOF / trailing-edge suppression — never emit for an error whose
//       offending token is `<EOF>` or whose start sits at/after the last
//       non-whitespace character (the user is mid-keystroke — the single largest
//       PARSE_SPIKE error class).
//   (c) First error per GO batch only — ANTLR recovery cascades after the first
//       genuine error; the follow-on errors are noise.
//   (d) Idle gating (debounce + MAX_SOURCE_LEN + model-version guard) lives in
//       the SqlMonaco layer, cloned from the type-check pass.
//
// Pure given `source` — no Monaco, no I/O beyond the parser loads. The
// SqlMonaco layer converts the returned offsets to Monaco positions via
// `model.getPositionAt`, so this module never touches Monaco and never
// re-derives line/column (zero off-by-one risk). Mirrors the shape of
// `typeCheck/collectTypeCheckMarkers.ts`.
//
// SPLIT (plan20 T2 × plan16 wedge fix): the ANTLR parse itself is WORKER-
// CARRIED in the live path — `buildSyntaxMarkersFromParseErrors` applies the
// ms-cheap suppression layers to errors the parse worker already produced
// (one-parse-two-products with the procedural-scope side table).
// `collectSyntaxMarkers` (which parses IN-PROCESS) remains for the Node-side
// unit + corpus suites only; see its doc block.

import { scanDocument } from '@/features/sql-grammar/document';
import type { SqlRange } from '@/features/sql-grammar/model';
import { collectTSqlParseErrors, type TSqlParseError } from '@/features/sql-grammar-tsql/parse';

import { buildErrorRecoveryView } from '../errorRecoveryViewBuilder';

export type SyntaxMarker = {
  /** Absolute UTF-16 code-unit offsets into the document source. */
  readonly startOffset: number;
  readonly endOffset: number;
  readonly message: string;
  /** Monaco marker `code` — namespaced so it never collides with LSP codes. */
  readonly code: 'tsql-syntax';
};

export type CollectSyntaxMarkersInput = {
  readonly source: string;
  /** Widened per-engine later; mssql-only today. */
  readonly engineId: 'mssql';
};

/** Node-test-side convenience wrapper (unit + corpus suites): runs the ANTLR
 *  parse IN-PROCESS, then applies the suppression pipeline. NEVER call this on
 *  the renderer main thread — a full-buffer ANTLR parse there measured 5–17 s
 *  on large files (the plan16 wedge class). The live path is worker-carried:
 *  `workerSyntaxErrorSource.ts` → `buildSyntaxMarkersFromParseErrors`. */
export async function collectSyntaxMarkers(
  input: CollectSyntaxMarkersInput,
): Promise<readonly SyntaxMarker[]> {
  const { source } = input;
  if (!source.trim()) return [];
  const antlrErrors = await collectTSqlParseErrors(source);
  return buildSyntaxMarkersFromParseErrors(source, antlrErrors);
}

/** The pure suppression pipeline (layers a–c) over PRE-COMPUTED ANTLR errors —
 *  the renderer-safe entry: everything here is ms-cheap (the tree-sitter view
 *  included); only the ANTLR parse producing `antlrErrors` is expensive, and
 *  in the live path it ran inside the parse worker. */
export async function buildSyntaxMarkersFromParseErrors(
  source: string,
  antlrErrors: readonly TSqlParseError[],
): Promise<readonly SyntaxMarker[]> {
  if (!source.trim()) return [];
  if (antlrErrors.length === 0) return [];

  // Layer (a) input: tree-sitter error-recovery ranges at confidence 1.0. A
  // null view (parser not loaded / mock) means "no second-parser signal" — the
  // AND-composition then suppresses everything, which is the safe direction.
  const view = await buildErrorRecoveryView('mssql', source);
  const treeSitterErrorRanges: readonly SqlRange[] = (view ?? [])
    .filter((e) => e.confidence >= 1)
    .map((e) => e.range);

  // Layer (b) input: offset just past the last non-whitespace character.
  const trailingEdge = source.trimEnd().length;

  // Layer (c) input: GO-batch separator offsets (execution-batch boundaries).
  const separatorOffsets = collectBatchSeparatorOffsets(source);

  const survivors = antlrErrors.filter((e) =>
    !isSuppressed(e, treeSitterErrorRanges, trailingEdge),
  );

  // Keep only the FIRST surviving error per GO batch (cascade suppression).
  const seenBatches = new Set<number>();
  const out: SyntaxMarker[] = [];
  for (const e of [...survivors].sort((a, b) => a.startOffset - b.startOffset)) {
    const batchIndex = batchIndexForOffset(e.startOffset, separatorOffsets);
    if (seenBatches.has(batchIndex)) continue;
    seenBatches.add(batchIndex);
    out.push({
      startOffset: e.startOffset,
      endOffset: e.endOffset,
      message: e.message,
      code: 'tsql-syntax',
    });
  }
  return out;
}

function isSuppressed(
  error: TSqlParseError,
  treeSitterErrorRanges: readonly SqlRange[],
  trailingEdge: number,
): boolean {
  // (b) EOF / trailing-edge — mid-keystroke incomplete statement.
  if (error.offendingToken === '<EOF>') return true;
  if (error.startOffset >= trailingEdge) return true;
  // (a) AND-of-two-parsers — require a tree-sitter error at an overlapping range.
  if (!overlapsAny(treeSitterErrorRanges, error)) return true;
  return false;
}

/** Inclusive-overlap test, matching the suppressor's `endOffset < start ||
 *  startOffset > end` exclusion. */
function overlapsAny(ranges: readonly SqlRange[], error: TSqlParseError): boolean {
  for (const r of ranges) {
    if (r.endOffset < error.startOffset) continue;
    if (r.startOffset > error.endOffset) continue;
    return true;
  }
  return false;
}

// GO-batch separator start offsets, ascending. Uses the canonical execution
// scanner so "batch" means the same thing here as it does at execution time
// (GO-in-string / GO-in-comment correctly ignored). Best-effort — a scan
// failure degrades to a single batch (every error shares index 0), which only
// ever OVER-suppresses.
function collectBatchSeparatorOffsets(source: string): number[] {
  try {
    const scan = scanDocument(source, { engine: 'mssql' });
    return scan.batchSeparators
      .map((s) => s.startOffset)
      .sort((a, b) => a - b);
  } catch {
    return [];
  }
}

function batchIndexForOffset(offset: number, separatorOffsets: readonly number[]): number {
  let index = 0;
  for (const sepOffset of separatorOffsets) {
    if (offset > sepOffset) index += 1;
    else break;
  }
  return index;
}
